import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { Text, Tabs, Box, Flex } from "@radix-ui/themes";
import { useStore } from '../store/store';
import Icon from '@mdi/react';
import { mdiEarth, mdiChat, mdiNote, mdiExport, mdiPoll, mdiArrowCollapseLeft, mdiCog } from '@mdi/js';
import SidebarWorld from './SidebarWorld';
import SidebarCharEdit from './SidebarCharEdit';
import SidebarLocEdit from './SidebarLocEdit';
import SidebarLoreEdit from './SidebarLoreEdit';
import SidebarNoteEdit from './SidebarNoteEdit';
import SidebarChat from './SidebarChat';
import SidebarChatContent from './SidebarChatContent';
import SidebarNotes from './SidebarNotes';
import SidebarExport from './SidebarExport';
import SidebarAnalytics from './SidebarAnalytics';
import SidebarAnalyticsScenes from './SidebarAnalyticsScenes';
import SidebarAnalyticsCharacters from './SidebarAnalyticsCharacters';
import SidebarAnalyticsCost from './SidebarAnalyticsCost';
import SidebarSettings from './SidebarSettings';

export const Sidebar = () => {
    const { novelId } = useParams();
    const [novel, setNovel] = useState(null);
    const [state, actions] = useStore();
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedLore, setSelectedLore] = useState(null);
    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedNote, setSelectedNote] = useState(null);
    const [showScenesAnalytics, setShowScenesAnalytics] = useState(false);
    const [showCharactersAnalytics, setShowCharactersAnalytics] = useState(false);
    const [showCostAnalytics, setShowCostAnalytics] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [defaultTab, setDefaultTab] = useState('world');

    const resetSideSidebar = () => {
        setSelectedCharacter(null);
        setSelectedLocation(null);
        setSelectedLore(null);
        setSelectedChat(null);
        setSelectedNote(null);
        setShowScenesAnalytics(false);
        setShowCharactersAnalytics(false);
        setShowCostAnalytics(false);
    }

    const toggleCollapsed = () => {
        resetSideSidebar();
        setCollapsed(!collapsed);
    }
    
    // Improved handlers for analytics sidebars to prevent flickering
    const handleOpenScenesAnalytics = () => {
        // Close all non-analytics sidebars first
        setSelectedCharacter(null);
        setSelectedLocation(null);
        setSelectedLore(null);
        setSelectedChat(null);
        setSelectedNote(null);
        
        // If we're already showing scenes analytics, do nothing
        if (showScenesAnalytics) return;
        
        // For smooth transition between analytics sidebars
        if (showCharactersAnalytics || showCostAnalytics) {
            // First set the current analytics to false and immediately open the new one
            setShowCharactersAnalytics(false);
            setShowCostAnalytics(false);
            setShowScenesAnalytics(true);
        } else {
            // If no analytics are open, just open scenes
            setShowScenesAnalytics(true);
        }
    };
    
    const handleOpenCharactersAnalytics = () => {
        // Close all non-analytics sidebars first
        setSelectedCharacter(null);
        setSelectedLocation(null);
        setSelectedLore(null);
        setSelectedChat(null);
        setSelectedNote(null);
        
        // If we're already showing characters analytics, do nothing
        if (showCharactersAnalytics) return;
        
        // For smooth transition between analytics sidebars
        if (showScenesAnalytics || showCostAnalytics) {
            // First set the current analytics to false and immediately open the new one
            setShowScenesAnalytics(false);
            setShowCostAnalytics(false);
            setShowCharactersAnalytics(true);
        } else {
            // If no analytics are open, just open characters
            setShowCharactersAnalytics(true);
        }
    };
    
    const handleOpenCostAnalytics = () => {
        // Close all non-analytics sidebars first
        setSelectedCharacter(null);
        setSelectedLocation(null);
        setSelectedLore(null);
        setSelectedChat(null);
        setSelectedNote(null);
        
        // If we're already showing cost analytics, do nothing
        if (showCostAnalytics) return;
        
        // For smooth transition between analytics sidebars
        if (showScenesAnalytics || showCharactersAnalytics) {
            // First set the current analytics to false and immediately open the new one
            setShowScenesAnalytics(false);
            setShowCharactersAnalytics(false);
            setShowCostAnalytics(true);
        } else {
            // If no analytics are open, just open cost
            setShowCostAnalytics(true);
        }
    };
    
    return (
        <>
            <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
            {!collapsed ?
                <Tabs.Root defaultValue={defaultTab}>
                    <Tabs.List color="cyan" style={{color: 'white'}}>
                        <Tabs.Trigger value="world" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiEarth} size={1} />
                        </Tabs.Trigger>
                        <Tabs.Trigger value="chat" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiChat} size={1} />
                        </Tabs.Trigger>
                        <Tabs.Trigger value="notes" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiNote} size={1} />
                        </Tabs.Trigger>
                        <Tabs.Trigger value="analytics" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiPoll} size={1} />
                        </Tabs.Trigger>
                        <Tabs.Trigger value="settings" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiCog} size={1} />
                        </Tabs.Trigger>
                        <Tabs.Trigger value="export" onClick={() => resetSideSidebar()}>
                            <Icon path={mdiExport} size={1} />
                        </Tabs.Trigger>
                    </Tabs.List>
                    <Box pt="3">
                        <Tabs.Content value="world">
                            <Text size="2">
                                <SidebarWorld 
                                resetSideSidebar={resetSideSidebar} 
                                selectedCharacter={selectedCharacter} 
                                setSelectedCharacter={setSelectedCharacter}
                                selectedLocation={selectedLocation}
                                setSelectedLocation={setSelectedLocation}
                                selectedLore={selectedLore}
                                setSelectedLore={setSelectedLore}
                                />
                            </Text>
                        </Tabs.Content>

                        <Tabs.Content value="chat">
                            <Text size="2">
                                <SidebarChat selectedChat={selectedChat} setSelectedChat={setSelectedChat}/>
                            </Text>
                        </Tabs.Content>

                        <Tabs.Content value="notes">
                            <Text size="2">
                                <SidebarNotes selectedNote={selectedNote} setSelectedNote={setSelectedNote}/>
                            </Text>
                        </Tabs.Content>

                        <Tabs.Content value="analytics">
                            <Text size="2">
                                <SidebarAnalytics
                                    onOpenScenesAnalytics={handleOpenScenesAnalytics}
                                    onOpenCharactersAnalytics={handleOpenCharactersAnalytics}
                                    onOpenCostAnalytics={handleOpenCostAnalytics}
                                />
                            </Text>
                        </Tabs.Content>

                        <Tabs.Content value="settings">
                            <Text size="2">
                                <SidebarSettings />
                            </Text>
                        </Tabs.Content>

                        <Tabs.Content value="export">
                            <Text size="2">
                                <SidebarExport />
                            </Text>
                        </Tabs.Content>

                    </Box>
                </Tabs.Root>
                : <>
                    <Flex className={"sidebar-collapsed-flex"} direction="column" align={'start'}>
                        <Icon onClick={() => {setDefaultTab('world'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiEarth} size={2} />
                        <Icon onClick={() => {setDefaultTab('chat'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiChat} size={2} />
                        <Icon onClick={() => {setDefaultTab('notes'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiNote} size={2} />
                        <Icon onClick={() => {setDefaultTab('analytics'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiPoll} size={2} />
                        <Icon onClick={() => {setDefaultTab('settings'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiCog} size={2} />
                        <Icon onClick={() => {setDefaultTab('export'); toggleCollapsed()}} className={"sidebar-collapsed-icon"} path={mdiExport} size={2} />
                    </Flex>
                </>}
                <Icon path={mdiArrowCollapseLeft} className={`sidebar-collapse-icon ${collapsed ? 'sidebar-collapse-icon-collapsed' : ''}`} onClick={() => toggleCollapsed()} size={1}/>
            </div>
            {selectedCharacter && <SidebarCharEdit selectedCharacter={selectedCharacter} setSelectedCharacter={setSelectedCharacter}/>}
            {selectedLocation && <SidebarLocEdit selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}/>}
            {selectedLore && <SidebarLoreEdit selectedLore={selectedLore} setSelectedLore={setSelectedLore}/>}
            {selectedNote && <SidebarNoteEdit selectedNote={selectedNote} setSelectedNote={setSelectedNote}/>}
            {selectedChat && <SidebarChatContent chat={selectedChat} setSelectedChat={setSelectedChat} />}
            {showScenesAnalytics && <SidebarAnalyticsScenes onClose={() => setShowScenesAnalytics(false)} />}
            {showCharactersAnalytics && <SidebarAnalyticsCharacters onClose={() => setShowCharactersAnalytics(false)} />}
            {showCostAnalytics && <SidebarAnalyticsCost onClose={() => setShowCostAnalytics(false)} />}
        </>
    );
};

export default Sidebar;